<template>
  <div :class="classForm" class="form-area">
    <div class="flex justify-between items-center">
      <h3 v-if="title">{{$t(title)}}</h3>
      <div class="form-top-area">
        <slot name="topArea"></slot>
      </div>
    </div>
    <slot name="beforeFields"></slot>
    <form v-if="errors.length > 0 && showMessageNotifications">
      <ul class="error-msgs">
        <li
          v-for="(error, i) in errorsTexts"
          :key="i"
        >
          {{ error }}
        </li>
      </ul>
    </form>
    <div :class="{mt30: title || errors.length > 0}">
      <app-form
        :id="id"
        :inputFields="inputFields"
        :customValidators="customValidators"
        :showMessageNotifications="showMessageNotifications"
        :model="formModel"
        :show-delete="showDelete"
        :disable-submit.sync="disableSubmit"
        :showSubmit="showSubmit"
        :submitButtonLabel="$t(submitButtonLabel)"
        :cancelButtonLabel="$t(cancelButtonLabel)"
        :submitButtonClass="submitButtonClass"
        :formElementClass="formElementClass"
        :formWrapperClass="formWrapperClass"
        :formGroupClass="formGroupClass"
        :showSaveAs="$attrs.showSaveAs"
        :showCancel="$attrs.showCancel"
        :validateFormTrigger="validateFormTrigger"
        :validateFieldTrigger="validateFieldTrigger"
        :showFooterBorderTop="$attrs.showFooterBorderTop"
        :loading="loading"
        :useDebounce="useDebounce"
        @onSearchChangeInput="onSearchChangeInput"
        @updateModel="updateModel"
        @onFormSubmit="save"
        @onDelete="deleteModel"
        @onCancelClick="cancel"
        @uploadComplete="uploadComplete"
        @onFieldBlur="({field, value, formId}) => $emit('onFieldBlur', {field, value, formId})"
        @validate="(messages) => $emit('validate', messages)"
      ><template v-for="(_, slot) in $slots">
          <template :slot="slot">
            <slot :name="slot"></slot>
          </template>
        </template>
      </app-form>
    </div>
  </div>
</template>

<script>
  import _ from 'lodash';

  import Form from './form';

  import { INPUT_FIELDS } from '../config/const';

  export default {
    props: {
      id: String,
      title: String,
      model: {
        type: Object,
        default: () => {},
      },
      fields: {
        type: Array,
        default: () => [],
        required: true
      },
      fieldsAdditions: {
        type: Object,
        default: () => {},
      },
      fieldsObjects: {
        type: Object,
        require: false,
        default: () => {
          return {};
        }
      },
      disableSubmit: {
        type: Boolean,
        require: false,
        default: false,
      },
      customValidators: {
        type: Object,
        default: () => {},
      },
      submitButtonLabel: {
        type: String,
        default: 'form.button.save'
      },
      cancelButtonLabel: {
        type: String,
        default: 'form.button.cancel'
      },
      submitButtonClass:  {
        type: String
      },
      classForm:  {
        type: String,
        required: false,
        default: '',
      },
      formElementClass:  {
        type: String,
        default: ''
      },
      formWrapperClass:  String,
      formGroupClass: {
        type: String,
        default: ''
      },
      errors: {
        type: Array,
        default: () => []
      },
      errorsType: {
        type: String,
        required: false,
        default: 'default'
      },
      loading: Boolean,
      showMessageNotifications: {
        type: Boolean,
        default: true
      },
      showDelete: {
        type: Boolean,
        required: false,
        default: false
      },
      showSubmit: {
        type: Boolean,
        default: true
      },
      validateFormTrigger: Boolean,
      validateFieldTrigger: {
        type: [Boolean, Object],
        default: false
      },
      useDebounce: {
        type: Boolean,
        default: true
      },
      syncModel: { // Trigger resolveModel when model is updated from parent
        type: Boolean,
        default: false
      }
    },
    components: {
      'app-form': Form
    },
    data: function () {
      return {
        inputFields: {},
        formModelObject: {},
        modelClone: {}
      }
    },
    created () {
      this.setInputFields()
      this.setFieldsCustomValidations()
      this.resolveModel()
    },
    watch: {
      fieldsAdditions: {
        handler (newValue, oldValue) {
          if(!_.isEqual(newValue, oldValue)) {
            this.setInputFields();
          }
        },
        deep: true
      },
      fieldsObjects: {
        handler (newValue, oldValue) {
          if(!_.isEqual(newValue, oldValue)) {
            this.setInputFields();
          }
        },
        deep: true
      },
      fields: {
        handler (newValue, oldValue) {
          if(!_.isEqual(newValue, oldValue)) {
            this.setInputFields();
          }
        },
        deep: true
      },
      model: {
        handler (newValue) {
          if(this.syncModel && !_.isEqual(newValue, this.modelClone)) {
            this.resolveModel();
            this.modelClone = _.cloneDeep(newValue);
          }
        },
        deep: true
      }
    },
    methods: {
      mapFieldsObject() {
        Object.keys(this.fieldsObjects).forEach((key) => {
          if (this.fieldsObjects[key].hasOwnProperty("value"))
            this.formModel[key] = this.fieldsObjects[key].value;
        });
      },
      setInputFields() {
        this.inputFields = {};
        
        this.fields.map(item => {
          if(typeof item === 'string' && INPUT_FIELDS[item]){
            const key = item;
            const value = INPUT_FIELDS[key];
            this.inputFields = {...this.inputFields, ...{[key]: _.cloneDeep(this.fieldsAdditions && this.fieldsAdditions[key] ? {...value, ...this.fieldsAdditions[key]} : value)}};
          } else if (item instanceof Object) {
            this.inputFields = { ...this.inputFields, ...{[item.key]: item } }
          }
        });

        if(this.fieldsObjects && Object.keys(this.fieldsObjects).length > 0){
          this.inputFields = {...this.inputFields, ...this.fieldsObjects}
        }
      },
      setFieldsCustomValidations() {
        this.clearCustomValidations();

        if (!_.isEmpty(this.customValidators)) {
          Object.keys(this.customValidators).forEach((fieldName) => {
            if(
              Object.keys(this.customValidators[fieldName]).length &&
              this.inputFields &&
              this.inputFields[fieldName] &&
              this.inputFields[fieldName].validate
            ) {
              this.inputFields[fieldName].validate.push('custom');
            }
          });
        }
      },

      clearCustomValidations() {
          Object.keys(this.inputFields).forEach((fieldName) => {
            if (this.inputFields[fieldName] && this.inputFields[fieldName].validate) {
              this.inputFields[fieldName].validate = this.inputFields[fieldName].validate.filter(item => {
                return item !== 'custom';
              })
            }
          });
      },

      resolveModel() {
        if(this.model) this.$set(this, 'formModel', this.model)
        if(this.fieldsObjects) this.mapFieldsObject()
      },
      onSearchChangeInput (value, field){
        this.$emit('onSearchChangeInput', value, field);
      },
      updateModel (model, field = null) {
        this.formModelObject = model;
        this.$set(this, 'formModel', model)
        this.$emit('updateModel', this.formModel, field)
      },
      uploadComplete() {
        this.$emit('uploadComplete')
      },
      save (saveAs) {
        if (saveAs)
          delete this.formModel.id;

        this.$emit('onSave', this.formModel, saveAs || false);
      },
      deleteModel (saveAs) {
        this.$emit('onDelete', this.formModel);
      },
      cancel () {
        this.$emit('onCancel');
      }
    },
    computed: {
      formModel: {
        get() { return this.formModelObject },
        set(newVal) {
          return this.formModelObject = newVal
        }
      },
      errorsTexts() {
        return this.errors.map(error => {
          return this.$t(`tag.form.errors.${this.errorsType}.${error}`)
        });
      }
    }
  }
</script>
